import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useAnalytics } from 'use-analytics';
import type { Error, ErrorCodes } from '../../utils/errorMapper';
import { QueryKey } from '@/Config/api/queryKey';
import { getPendingVerificationEmail } from '@/Domain/utils/pendingVerificationEmail';

type EmailVerificationDto = {
  user_email: string;
};

export const useVerificationEmail = () => {
  const { track } = useAnalytics();
  const userEmail = getPendingVerificationEmail();

  const [showResendMessage, setShowResendMessage] = useState(false);
  const [apiError, setApiError] = useState<ErrorCodes | undefined>();

  const mutation = useMutation<unknown, Error<EmailVerificationDto>, EmailVerificationDto>({
    mutationKey: [QueryKey.accounts.sendVerification],
    onSuccess: () => {
      track('email_reverification_success', {
        event_category: 'User verification',
        event_label: 'email_verification',
      });
      setShowResendMessage(true);
      setApiError(undefined);
    },
    onError: (error) => {
      track('email_reverification_error', {
        event_category: 'User verification',
        event_label: 'email_verification',
      });

      if (error.response) {
        let errorMessage;
        setShowResendMessage(false);

        Object.entries(error.response?.data?.errors).forEach(([key, value]) => {
          if (key === 'root') {
            value.forEach((errorDetail) => {
              errorMessage = errorDetail.code;
            });
          }
        });

        setApiError(errorMessage);
      }
    },
  });

  const handleResendClick = (): void => {
    if (userEmail !== null) {
      track('email_reverification_send', {
        event_category: 'User verification',
        event_label: 'email_verification',
      });

      mutation.mutate({ user_email: userEmail });
    }
  };

  return {
    apiError,
    handleResendClick,
    showResendMessage,
    isPending: mutation.isPending,
  };
};
